<template>
  <div class="export-panel" :class="{ show }">
    <section class="panel-section">
      <p class="section-label">{{ $t('exportPanel.export') }}</p>
      <div class="export-formats">
        <button
          v-for="format in exportFormats"
          :key="format"
          class="export-chip"
          type="button"
          @click="exportAs(format)"
        >
          {{ format.toUpperCase() }}
        </button>
      </div>
      <button v-if="allowAudioDownload" class="tool-row" type="button" @click="downloadAudio">
        <span class="tool-icon tool-icon--audio"></span>
        {{ $t('exportPanel.downloadAudio') }}
      </button>
    </section>

    <section v-if="ownFile" class="panel-section">
      <button class="tool-row tool-row--danger" type="button" @click="showDeleteConfirm">
        <span class="tool-icon tool-icon--delete"></span>
        {{ $t('exportPanel.deleteFile') }}
      </button>
    </section>
  </div>
</template>

<script>
import { inject } from 'vue'
import { Document, Packer, Paragraph, TextRun } from 'docx'
import { useI18n } from 'vue-i18n'
import { formatTimeHHMMSS, formatTimeSRT } from '@/utils/format'

export default {
  name: 'ExportPanel',
  props: {
    subtitles: {
      type: Array,
      required: true
    },
    audioName: {
      type: String,
      required: true
    },
    audioId: {
      type: String,
      required: true
    },
    show: {
      type: Boolean,
      default: false
    },
    allowAudioDownload: {
      type: Boolean,
      default: true
    },
    ownFile: {
      type: Boolean,
      default: true
    },
  },
  emits: [
    'downloadAudio',
    'deleteFile',
    'export',
  ],
  setup(props, { emit }) {
    const { t } = useI18n()
    const dialog = inject('dialog')
    const exportFormats = ['docx', 'txt', 'csv', 'srt', 'vtt']

    async function exportAs(format) {
      try {
        let content, type, filename
        const baseFilename = props.audioName.replace(/\.[^/.]+$/, '')

        switch (format) {
          case 'docx': {
            const doc = new Document({
              sections: [{
                properties: {},
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: baseFilename,
                        bold: true,
                        size: 32
                      })
                    ]
                  }),
                  ...props.subtitles.map(subtitle =>
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${formatTime(subtitle.start)} --> ${formatTime(subtitle.end)}`,
                          size: 20,
                          color: '666666'
                        }),
                        new TextRun({
                          text: '\n' + subtitle.text + '\n\n',
                          size: 24
                        })
                      ]
                    })
                  )
                ]
              }]
            })
            const buffer = await Packer.toBuffer(doc)
            content = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
            filename = baseFilename + '.docx'
            break
          }

          case 'txt':
            content = props.subtitles.map(subtitle =>
              `${formatTime(subtitle.start)} --> ${formatTime(subtitle.end)}\n${subtitle.text}\n\n`
            ).join('')
            type = 'text/plain'
            filename = baseFilename + '.txt'
            break

          case 'csv':
            content = 'Start,End,Text\n' + props.subtitles.map(subtitle =>
              `"${formatTime(subtitle.start)}","${formatTime(subtitle.end)}","${subtitle.text.replace(/"/g, '""')}"`
            ).join('\n')
            type = 'text/csv'
            filename = baseFilename + '.csv'
            break

          case 'srt':
            content = props.subtitles.map((subtitle, index) =>
              `${index + 1}\n${formatTime(subtitle.start, true)} --> ${formatTime(subtitle.end, true)}\n${subtitle.text}\n\n`
            ).join('')
            type = 'text/plain'
            filename = baseFilename + '.srt'
            break

          case 'vtt':
            content = 'WEBVTT\n\n' + props.subtitles.map(subtitle =>
              `${formatTime(subtitle.start, true)} --> ${formatTime(subtitle.end, true)}\n${subtitle.text}\n\n`
            ).join('')
            type = 'text/plain'
            filename = baseFilename + '.vtt'
            break
        }

        if (!(content instanceof Blob)) {
          content = new Blob([content], { type })
        }

        const url = URL.createObjectURL(content)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        emit('export', { format, filename })
      } catch (error) {
        dialog.error(t('common.exportFailed'))
      }
    }

    function formatTime(seconds, withMs = false) {
      if (withMs) {
        return formatTimeSRT(seconds)
      }
      return formatTimeHHMMSS(seconds)
    }

    function downloadAudio() {
      emit('downloadAudio')
    }

    function showDeleteConfirm() {
      dialog.openDeleteDialog({ emit }, props.audioName)
    }

    return {
      exportFormats,
      exportAs,
      downloadAudio,
      showDeleteConfirm,
    }
  }
}
</script>

<style scoped>
/* 停靠在右侧，和左侧边栏对称：纸色底，只用一条细线分隔 */
.export-panel {
  box-sizing: border-box;
  position: absolute;
  top: var(--navbar-height);
  right: 0;
  bottom: 0;
  z-index: 10;
  width: var(--sidebar-width);
  min-width: var(--sidebar-width);
  overflow-y: auto;
  padding: var(--space-1) var(--space-3);
  border-left: 1px solid var(--border-subtle);
  background: var(--color-page);
}

/* 分组之间只用细分隔线，不再折叠 */
.panel-section {
  display: grid;
  gap: var(--space-1);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--border-subtle);
}

.panel-section:last-child {
  border-bottom: 0;
}

.section-label {
  margin: 0;
  padding: 0 var(--space-3);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 600;
  line-height: 1.5;
}

/* 工具行 */
.tool-row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  min-height: 36px;
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  text-align: left;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}

.tool-row:hover {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.tool-row--danger:hover {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

.tool-icon {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  background: var(--ink-tertiary);
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

.tool-row:hover .tool-icon {
  background: var(--ink-secondary);
}

.tool-row--danger:hover .tool-icon {
  background: currentColor;
}

.tool-icon--audio {
  mask-image: url('~@/assets/ui-icons/volume.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/volume.svg');
}

.tool-icon--delete {
  mask-image: url('~@/assets/ui-icons/close.svg');
  -webkit-mask-image: url('~@/assets/ui-icons/close.svg');
}

/* 导出格式 */
.export-formats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3) var(--space-2);
}

.export-chip {
  height: 32px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
  font-weight: 500;
  cursor: pointer;
  transition: background 0.16s ease, border-color 0.16s ease;
}

.export-chip:hover {
  border-color: var(--border-active);
  background: var(--surface-subtle);
}

.tool-row:focus-visible,
.export-chip:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

/* 窄屏放不下并排的栏，改成从右侧滑出的浮层 */
@media (max-width: 1100px) {
  .export-panel {
    position: fixed;
    /* 从播放页标题栏下方开始，不挡住右上角的开关按钮 */
    top: calc(var(--navbar-height) + 60px);
    right: calc(-1 * var(--sidebar-width));
    background: var(--surface-panel);
    box-shadow: var(--shadow-md);
    transition: right 0.24s ease;
  }

  .export-panel.show {
    right: 0;
  }
}

@media (max-width: 768px) {
  .export-panel {
    top: 3.5rem;
  }
}
</style>

<style>
.player-select-popper.el-popper {
  max-width: calc(100vw - 24px);
  padding: var(--space-1) 0;
  overflow: hidden;
  background: var(--surface-panel);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-float);
}

.player-select-popper .el-popper__arrow {
  display: none;
}

.player-select-popper .el-select-dropdown__wrap {
  max-height: 304px;
}

.player-select-popper .el-select-dropdown__item {
  height: 36px;
  margin: 0 var(--space-1);
  padding: 0 var(--space-3);
  color: var(--ink-primary);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  line-height: 36px;
}

.player-select-popper .el-select-dropdown__item.is-hovering {
  background: var(--surface-subtle);
}

.player-select-popper .el-select-dropdown__item.is-selected {
  color: var(--signal-active-strong);
  background: var(--surface-active);
  font-weight: 600;
}
</style>
